'use client'
import { createContext, useContext, useEffect, useState } from 'react'
import type { WhatsUpResponse } from './entities'
import { whatsUp } from './service'
import { useErrorHandling } from '@/tools/o11y/client'

type WhatsupContextType = Partial<WhatsUpResponse> & {
  getWhatsUp: () => Promise<void>
}

const WhatsUpContext = createContext<WhatsupContextType>({
  phoneVerificationRequired: false,
  getWhatsUp: async () => {},
})

export function WhatsUpProvider({ children }: { children: React.ReactNode }) {
  const [whatsUpState, setWhatsupState] = useState<WhatsUpResponse | null>(null)
  const { execWithErrorTracking } = useErrorHandling()

  const getWhatsUp = async () => {
    const response = await whatsUp('phoneVerificationRequired')
    setWhatsupState(response)
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      await getWhatsUp()
    })
  }, [execWithErrorTracking])

  const value: WhatsupContextType = {
    ...whatsUpState,
    getWhatsUp,
  }

  return <WhatsUpContext.Provider value={value}>{children}</WhatsUpContext.Provider>
}

export function useWhatsUp() {
  const context = useContext(WhatsUpContext)
  if (!context) {
    throw new Error('useWhatsUp must be used inside a <WhatsupContext>')
  }
  return context
}
